import { useMemo } from 'react';
import { Wifi, Monitor, Smartphone, Tablet, Globe, Zap } from 'lucide-react';
import { formatCompact } from '@/lib/utils';
import { useGA4DeviceBreakdown, useGA4ActiveUsers } from '@/hooks/useGA4ActivityData';
import { useAuthStore } from '@/stores/auth-store';

const DEVICE_COLORS = {
  desktop: '#3b82f6', // blue
  mobile: '#ec4899', // pink
  tablet: '#06b6d4', // cyan
  other: '#10b981', // emerald
};

const DEVICE_ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  other: Globe,
};

/**
 * Live Pulse View - Radial visualization of active users by device
 * Inspired by biological/radar visualization style
 */
export function LivePulseView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: deviceData, isLoading: devicesLoading } = useGA4DeviceBreakdown();
  const { data: activeUsers, isLoading: activeLoading } = useGA4ActiveUsers();

  const isLoading = devicesLoading || activeLoading;

  // Normalize device categories
  const normalizedDevices = useMemo(() => {
    if (!deviceData?.devices) return [];

    return deviceData.devices.map((d) => {
      const deviceLower = d.device.toLowerCase();
      let category: keyof typeof DEVICE_COLORS = 'other';

      if (deviceLower.includes('desktop')) category = 'desktop';
      else if (deviceLower.includes('mobile')) category = 'mobile';
      else if (deviceLower.includes('tablet')) category = 'tablet';

      return {
        ...d,
        category,
        color: DEVICE_COLORS[category],
        Icon: DEVICE_ICONS[category],
      };
    });
  }, [deviceData]);

  // Find dominant device
  const dominantDevice = normalizedDevices[0];

  if (!hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Wifi className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Connect GA4 to see live pulse</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Wifi className="h-6 w-6 text-primary" />
            Live Pulse
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Active users by device
          </p>
        </div>

        {/* Active Users Badge */}
        <div className="flex items-center gap-2 bg-success/20 px-3 py-1.5 rounded-full">
          <Zap className="h-4 w-4 text-success animate-pulse" />
          <span className="text-sm font-bold text-success">
            {activeUsers?.toLocaleString() || '...'} active
          </span>
        </div>
      </div>

      {/* Main Visualization */}
      <div className="glass-panel p-6 flex-1 flex items-center justify-center">
        {isLoading ? (
          <div className="text-muted-foreground animate-pulse">
            Loading device data...
          </div>
        ) : normalizedDevices.length === 0 ? (
          <div className="text-center">
            <Wifi className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No device data available</p>
          </div>
        ) : (
          <div className="flex items-center gap-12">
            {/* Radial Visualization */}
            <div className="relative w-80 h-80">
              {/* Concentric rings */}
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Background rings */}
                {[80, 60, 40, 20].map((r, i) => (
                  <circle
                    key={i}
                    cx="100"
                    cy="100"
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-border opacity-30"
                  />
                ))}

                {/* Device arcs */}
                {normalizedDevices.map((device, index) => {
                  const radius = 80 - index * 15;
                  const arcLength = (device.percentage / 100) * 360;
                  const startAngle = -90; // Start from top

                  // Calculate arc path
                  const x1 = 100 + radius * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 100 + radius * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 100 + radius * Math.cos(((startAngle + arcLength) * Math.PI) / 180);
                  const y2 = 100 + radius * Math.sin(((startAngle + arcLength) * Math.PI) / 180);

                  const largeArcFlag = arcLength > 180 ? 1 : 0;

                  const pathD = arcLength >= 360
                    ? `M ${100 - radius} 100 A ${radius} ${radius} 0 1 1 ${100 + radius} 100 A ${radius} ${radius} 0 1 1 ${100 - radius} 100`
                    : `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

                  return (
                    <path
                      key={device.category}
                      d={pathD}
                      fill="none"
                      stroke={device.color}
                      strokeWidth="8"
                      strokeLinecap="round"
                      className="drop-shadow-glow"
                      style={{
                        filter: `drop-shadow(0 0 6px ${device.color}40)`,
                      }}
                    />
                  );
                })}

                {/* Center glow */}
                <circle
                  cx="100"
                  cy="100"
                  r="25"
                  fill="url(#centerGlow)"
                  className="animate-pulse"
                />

                <defs>
                  <radialGradient id="centerGlow">
                    <stop offset="0%" stopColor={dominantDevice?.color || '#3b82f6'} stopOpacity="0.6" />
                    <stop offset="100%" stopColor={dominantDevice?.color || '#3b82f6'} stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>

              {/* Center text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span
                  className="text-4xl font-bold"
                  style={{ color: dominantDevice?.color }}
                >
                  {dominantDevice?.percentage.toFixed(0)}%
                </span>
                <span className="text-sm text-muted-foreground uppercase tracking-wider">
                  {dominantDevice?.category}
                </span>
              </div>
            </div>

            {/* Device Legend */}
            <div className="space-y-4">
              {normalizedDevices.map((device) => {
                const Icon = device.Icon;
                return (
                  <div key={device.category} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: device.color }}
                    />
                    <Icon
                      className="h-5 w-5"
                      style={{ color: device.color }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-8">
                        <span className="text-sm font-medium text-foreground capitalize">
                          {device.category}
                        </span>
                        <span className="text-sm font-bold" style={{ color: device.color }}>
                          {device.percentage.toFixed(0)}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatCompact(device.users)} users
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Device Stats Grid */}
      {normalizedDevices.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          {normalizedDevices.slice(0, 4).map((device) => {
            const Icon = device.Icon;
            return (
              <DeviceStatCard
                key={device.category}
                icon={Icon}
                label={device.category}
                users={device.users}
                sessions={device.sessions}
                percentage={device.percentage}
                color={device.color}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface DeviceStatCardProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  users: number;
  sessions: number;
  percentage: number;
  color: string;
}

function DeviceStatCard({ icon: Icon, label, users, sessions, percentage, color }: DeviceStatCardProps) {
  return (
    <div className="glass-panel p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-5 w-5" style={{ color }} />
        <span className="text-sm font-medium text-foreground capitalize">{label}</span>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Users</span>
          <span className="text-sm font-bold" style={{ color }}>
            {formatCompact(users)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Sessions</span>
          <span className="text-sm text-foreground">{formatCompact(sessions)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Share</span>
          <span className="text-sm text-foreground">{percentage.toFixed(1)}%</span>
        </div>
      </div>
      {/* Mini progress bar */}
      <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
